import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CheckIcon, PlusIcon, XIcon } from 'lucide-react';
import { useAppState } from '../contexts/AppStateContext';
import { useUi } from '../contexts/UiContext';
import { moduleLabels } from '../data/labels';
import { ModuleKey } from '../types/family';
import { WizardMember } from '../utils/familyFactory';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Chip, Field, Toggle } from '../components/ui/Form';
const steps = ['Ime', 'Člani', 'Moduli', 'Začetek', 'Pregled'];
const defaultModules: Record<ModuleKey, boolean> = {
  hisnaOpravila: true,
  osebnaRutina: true,
  sola: true,
  zivali: false,
  glasba: false,
  vecerniRezim: true,
  nagrade: true,
  skupniSeznami: true
};
export function FamilyWizard() {
  const {
    viewer,
    createFamilyAndSwitch
  } = useAppState();
  const {
    openSub
  } = useUi();
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [members, setMembers] = useState<WizardMember[]>([{
    name: viewer.name,
    role: 'skrbnik'
  }]);
  const [modules, setModules] = useState(defaultModules);
  const [start, setStart] = useState<'prazno' | 'predloga'>('predloga');
  const valid = [name.trim().length > 0, members.every(m => m.name.trim()) && members.some(m => m.role === 'skrbnik'), true, true, true][step];
  const setMember = (i: number, p: Partial<WizardMember>) => setMembers(ms => ms.map((m, j) => j === i ? {
    ...m,
    ...p
  } : m));
  const finish = () => {
    createFamilyAndSwitch({
      name,
      members,
      modules,
      start
    });
    openSub({
      kind: 'settings'
    });
  };
  return <div className="mx-auto max-w-2xl" data-id="element-1457" data-mp="1g5pokv4nc1rd">
      <PageHeader title="Ustvari novo družino" subtitle="Nova družina je popolnoma ločena: brez podatkov, zgodovine ali koledarjev drugih družin." onBack={() => openSub(null)} data-id="element-1458" data-mp="1c29x0d7forlj" />
      <ol className="mb-6 flex gap-2" aria-label="Koraki" data-id="element-1459" data-mp="175swskmymms3">
        {steps.map((s, i) => <li key={s} className="flex-1" data-id="element-1460" data-mp="xhxfupmuqqsq" data-mpi={i}>
            <div className={`h-2 rounded-full ${i <= step ? 'bg-accent' : 'bg-line'}`} data-id="element-1461" data-mp="11hxuobdv540b" />
            <p className={`mt-1 text-xs font-bold ${i === step ? 'text-ink' : 'text-muted'}`} aria-current={i === step ? 'step' : undefined} data-id="element-1462" data-mp="nhwu6x1ifyld">
              {s}
            </p>
          </li>)}
      </ol>

      <AnimatePresence mode="wait" initial={false} data-id="element-1463" data-mp="1g62cld53dbi1">
        <motion.div key={step} initial={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="rounded-3xl border-[1.5px] border-line bg-surface p-5 md:p-6" data-id="element-1464" data-mp="1g8f2qdl06s66">
          {step === 0 && <Field label="Kako se imenuje vaša družina?" hint="Ime družine je ločeno od imena aplikacije. Ti postaneš skrbnik." data-id="element-1465" data-mp="1z3daouezlyvw">
              {id => <input id={id} autoFocus className="field" value={name} onChange={e => setName(e.target.value)} placeholder="npr. Novak" data-id="element-1466" data-mp="3olikqqk9cein" />}
            </Field>}

          {step === 1 && <div className="space-y-3" data-id="element-1467" data-mp="19hjnaalvzls">
              {members.map((m, i) => <div key={i} className="rounded-2xl bg-canvas p-3" data-id="element-1468" data-mp="3tdaef9ypn0d0" data-mpi={i}>
                  <div className="flex items-center gap-2" data-id="element-1469" data-mp="3tf4ykm25qog1">
                    <input className="field" aria-label={`Ime člana ${i + 1}`} value={m.name} onChange={e => setMember(i, {
                name: e.target.value
              })} placeholder="Ime" data-id="element-1470" data-mp="35ohc8vrhvw9y" />
                    {i > 0 && <button type="button" aria-label="Odstrani člana" onClick={() => setMembers(ms => ms.filter((_, j) => j !== i))} className="grid h-11 w-11 shrink-0 place-items-center rounded-xl text-muted hover:bg-surface" data-id="element-1471" data-mp="3eoxfq588n3et">
                        <XIcon className="h-4 w-4" data-id="element-1472" data-mp="tzzwh314tfce" />
                      </button>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-1473" data-mp="1k98qkev5tn5e">
                    <Chip active={m.role === 'skrbnik'} onClick={() => setMember(i, {
                role: 'skrbnik'
              })} data-id="element-1474" data-mp="1kbd2nk37psgr">
                      Skrbnik
                    </Chip>
                    <Chip active={m.role === 'otrok'} onClick={() => setMember(i, {
                role: 'otrok'
              })} data-id="element-1475" data-mp="1u6dg8oo7s0qy">
                      Otrok
                    </Chip>
                    {i === 0 ? <span className="text-xs font-bold text-muted" data-id="element-1476" data-mp="23736lcupgyxa">to si ti</span> : <input className="field h-9 flex-1 text-sm" aria-label={`Kontakt za vabilo: ${m.name || 'član'}`} value={m.contact ?? ''} onChange={e => setMember(i, {
                contact: e.target.value
              })} placeholder="e-pošta ali telefon za vabilo" data-id="element-1477" data-mp="285d25okce1jf" />}
                  </div>
                </div>)}
              <Button variant="secondary" onClick={() => setMembers(ms => [...ms, {
            name: '',
            role: 'otrok'
          }])} data-id="element-1478" data-mp="2mu0w5qeji379">
                <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-1479" data-mp="f9u0simkbspq" /> Član
              </Button>
              <p className="text-xs text-muted" data-id="element-1480" data-mp="b8i8248e6rdb">Člane lahko dodaš tudi pozneje.</p>
            </div>}

          {step === 2 && <div className="divide-y divide-line" data-id="element-1481" data-mp="6agrbpyfv450">
              {(Object.keys(moduleLabels) as ModuleKey[]).map(k => <Toggle key={k} checked={modules[k]} onChange={v => setModules(m => ({
            ...m,
            [k]: v
          }))} label={moduleLabels[k].label} hint={moduleLabels[k].hint} data-id="element-1482" data-mp="1c5ct1x785gg" />)}
            </div>}

          {step === 3 && <div className="grid gap-3 sm:grid-cols-2" data-id="element-1483" data-mp="p7zy0mxfcao9">
              {([['predloga', 'Prilagodljiva predloga', 'Nevtralne kategorije, cilji in nagrade s predlaganimi vrednostmi, ki jih pregledaš.'], ['prazno', 'Prazno', 'Začneš od nič in vse nastaviš sam.']] as const).map(([v, label, hint]) => <button key={v} type="button" onClick={() => setStart(v)} aria-pressed={start === v} className={`rounded-3xl border-2 p-4 text-left transition-colors duration-150 ${start === v ? 'border-accent bg-accent-soft/50' : 'border-line hover:bg-canvas'}`} data-id="element-1484" data-mp="375ohkg1wc7be">
                  <span className="flex items-center justify-between font-display text-lg font-semibold" data-id="element-1485" data-mp="32cvahliygmw5">
                    {label} {start === v && <CheckIcon className="h-5 w-5 text-accent" aria-hidden="true" data-id="element-1486" data-mp="2sgjx0s7mpr04" />}
                  </span>
                  <span className="mt-1 block text-sm text-muted" data-id="element-1487" data-mp="2ehk59botkvzq">{hint}</span>
                </button>)}
              <p className="text-xs text-muted sm:col-span-2" data-id="element-1488" data-mp="2jh54ln7wj6cz">Osebni podatki, zgodovina, fotografije in koledarji drugih družin se nikoli ne kopirajo.</p>
            </div>}

          {step === 4 && <dl className="space-y-2 text-sm" data-id="element-1489" data-mp="hnfqb9e8rwfh">
              <div className="flex justify-between gap-3" data-id="element-1490" data-mp="rsp2sawyumnp">
                <dt className="text-muted" data-id="element-1491" data-mp="rt72ximr1l0w">Družina</dt>
                <dd className="font-bold" data-id="element-1492" data-mp="mu4b4zvs5bav">{name}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1493" data-mp="mrrklybyhsm7">
                <dt className="text-muted" data-id="element-1494" data-mp="10lsm2zualtq9">Člani</dt>
                <dd className="text-right font-bold" data-id="element-1495" data-mp="vuiye4sb10h0">{members.map(m => `${m.name} (${m.role})`).join(', ')}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1496" data-mp="1ai41vzt3d1pg">
                <dt className="text-muted" data-id="element-1497" data-mp="15k2hnbjxiwce">Moduli</dt>
                <dd className="text-right font-bold" data-id="element-1498" data-mp="15jkdlsr0zdtx">
                  {(Object.keys(modules) as ModuleKey[]).filter(k => modules[k]).map(k => moduleLabels[k].label).join(', ')}
                </dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1499" data-mp="3lc9njy6m82x3">
                <dt className="text-muted" data-id="element-1500" data-mp="3h9mrfl2wvvu3">Začetek</dt>
                <dd className="font-bold" data-id="element-1501" data-mp="3hb7kt7dhp8qd">{start === 'prazno' ? 'Prazno' : 'Prilagodljiva predloga'}</dd>
              </div>
              <div className="flex justify-between gap-3" data-id="element-1502" data-mp="9ncklymm37a5">
                <dt className="text-muted" data-id="element-1503" data-mp="emyy896cqpjh">Vabila</dt>
                <dd className="font-bold" data-id="element-1504" data-mp="jeypld8cwa4g">{members.filter((m, i) => i > 0 && m.contact).length} pripravljenih (vzorčno, brez pošiljanja)</dd>
              </div>
            </dl>}
        </motion.div>
      </AnimatePresence>

      <div className="mt-5 flex justify-between gap-3" data-id="element-1505" data-mp="3mwgvvg8hd92r">
        <Button variant="secondary" disabled={step === 0} onClick={() => setStep(s => s - 1)} data-id="element-1506" data-mp="3my1upi1tve6f">
          Nazaj
        </Button>
        {step < steps.length - 1 ? <Button disabled={!valid} onClick={() => setStep(s => s + 1)} data-id="element-1507" data-mp="3vuu2blpnlmte">
            Naprej
          </Button> : <Button variant="success" onClick={finish} data-id="element-1508" data-mp="4npwgu58w6mk">
            Ustvari družino in pošlji vabila
          </Button>}
      </div>
    </div>;
}